import api from './api';
import {
  EmpresaRequestDTO,
  EmpresaResponseDTO,
  PageEmpresaResponseDTO,
  CompanySearchParams,
  ApiResponse
} from '../types/company';

class CompanyService {
  private baseUrl = '/v1/empresas';

  // Listar empresas com paginação
  async getCompanies(params?: CompanySearchParams): Promise<PageEmpresaResponseDTO> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params?.page !== undefined) queryParams.append('page', params.page.toString());
      if (params?.size !== undefined) queryParams.append('size', params.size.toString());
      if (params?.sort) queryParams.append('sort', params.sort);
      if (params?.direction) queryParams.append('direction', params.direction);
      
      // Adicionar filtros se existirem
      if (params?.filters) {
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== '') {
            queryParams.append(key, value.toString());
          }
        });
      }

      const url = queryParams.toString() ? `${this.baseUrl}/paginado?${queryParams}` : `${this.baseUrl}/paginado`;
      const response = await api.get<PageEmpresaResponseDTO>(url);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      throw error;
    }
  }

  // Buscar empresa por ID
  async getCompanyById(id: string): Promise<EmpresaResponseDTO> {
    try {
      const response = await api.get<EmpresaResponseDTO>(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar empresa por ID:', error);
      throw error;
    }
  }

  // Criar nova empresa
  async createCompany(company: EmpresaRequestDTO): Promise<EmpresaResponseDTO> {
    try {
      const response = await api.post<EmpresaResponseDTO>(this.baseUrl, company);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar empresa:', error);
      throw error;
    }
  }

  // Atualizar empresa
  async updateCompany(id: string, company: EmpresaRequestDTO): Promise<EmpresaResponseDTO> {
    try {
      const response = await api.put<EmpresaResponseDTO>(`${this.baseUrl}/${id}`, company);
      return response.data;
    } catch (error) {
      console.error('Erro ao atualizar empresa:', error);
      throw error;
    }
  }

  // Inativar empresa (soft delete)
  async deleteCompany(id: string): Promise<void> {
    try {
      await api.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      console.error('Erro ao inativar empresa:', error);
      throw error;
    }
  }

  // Buscar empresas por termo
  async searchCompanies(searchTerm: string): Promise<EmpresaResponseDTO[]> {
    try {
      const response = await api.get<EmpresaResponseDTO[]>(`${this.baseUrl}/buscar?termo=${encodeURIComponent(searchTerm)}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      throw error;
    }
  }

  // Obter empresa matriz
  async getMatrizCompany(): Promise<EmpresaResponseDTO> {
    try {
      const response = await api.get<EmpresaResponseDTO>(`${this.baseUrl}/matriz`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar empresa matriz:', error);
      throw error;
    }
  }

  // Obter filiais
  async getFiliais(): Promise<EmpresaResponseDTO[]> {
    try {
      const response = await api.get<EmpresaResponseDTO[]>(`${this.baseUrl}/filiais`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar filiais:', error);
      throw error;
    }
  }

  // Obter empresas do usuário logado
  async getMyCompanies(): Promise<EmpresaResponseDTO[]> {
    try {
      const response = await api.get<EmpresaResponseDTO[]>(`${this.baseUrl}/me/empresas`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar minhas empresas:', error);
      throw error;
    }
  }

  // Obter empresas por usuário
  async getCompaniesByUser(usuarioId: string): Promise<EmpresaResponseDTO[]> {
    try {
      const response = await api.get<EmpresaResponseDTO[]>(`${this.baseUrl}/usuario/${usuarioId}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao buscar empresas do usuário:', error);
      throw error;
    }
  }

  // Validar CNPJ
  validateCNPJ(cnpj: string): boolean {
    // Remove caracteres não numéricos
    const cleanCNPJ = cnpj.replace(/[^\d]/g, '');
    
    // Verifica se tem 14 dígitos
    if (cleanCNPJ.length !== 14) return false;
    
    // Verifica se não são todos os dígitos iguais
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;
    
    // Validação dos dígitos verificadores
    let soma = 0;
    let peso = 2;
    
    // Primeiro dígito verificador
    for (let i = 11; i >= 0; i--) {
      soma += parseInt(cleanCNPJ[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    let digito1 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    
    if (parseInt(cleanCNPJ[12]) !== digito1) return false;
    
    // Segundo dígito verificador
    soma = 0;
    peso = 2;
    
    for (let i = 12; i >= 0; i--) {
      soma += parseInt(cleanCNPJ[i]) * peso;
      peso = peso === 9 ? 2 : peso + 1;
    }
    
    let digito2 = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    
    return parseInt(cleanCNPJ[13]) === digito2;
  }

  // Formatar CNPJ
  formatCNPJ(cnpj: string): string {
    const cleanCNPJ = cnpj.replace(/[^\d]/g, '');
    if (cleanCNPJ.length === 14) {
      return cleanCNPJ.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return cnpj;
  }

  // Remover formatação do CNPJ
  cleanCNPJ(cnpj: string): string {
    return cnpj.replace(/[^\d]/g, '');
  }
}

const companyService = new CompanyService();
export default companyService;